import { cloneEmailTemplateThunk } from '@spike/email-marketing-action';
import { EmailTemplate } from '@spike/email-marketing-model';
import EmailTemplateEditorComponent from 'components/EmailMarketing/EmailTemplateEditor/EmailTemplateEditorComponent';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { EmailCampaignStepProps } from '../../types';
import TemplateStepForm from './TemplateStepForm';
import TemplateStepPicker from './TemplateStepPicker';
import styles from './TemplateStep.module.scss';

type EmbeddedEditor = { mode: 'create' } | { mode: 'edit'; templateId: number } | null;

interface TemplateStepProps extends EmailCampaignStepProps {
    /** Opens the wizard-root EmailSender drawer — wired from every CTA in the
     *  "Sending from" area of the form. */
    onOpenSenderDrawer: () => void;
}

/**
 * Step 2 of the campaign wizard — Template.
 *
 * Layout: two columns inside the wizard body. Left = campaign form (Subject,
 * Preview text, Sender Name, "Sending from" section). Right = picker (Email
 * Saved / Template Gallery / selected preview).
 *
 * The embedded editor (Klaviyo-style overlay) is rendered as a sibling div
 * whose component (EmailTemplateEditorComponent) is already `position: fixed`
 * with z-index 1300 — it visually covers the wizard while keeping all wizard
 * state alive in React.
 *
 * Sender Name is prefilled with the marketplace's business name at wizard-
 * state init (see `AddEmailCampaign`), not here. Prefilling on step mount
 * doesn't stick — the V3 <Input> in TemplateStepForm caches its initial
 * value in useState and ignores later prop updates. The previous Sender
 * Email input is gone — the effective sender is now decided at the
 * marketplace level via the Email Sender tab. `values.senderEmail` still
 * exists on the form values for backwards-compat (draft resume) but is not
 * user-editable here.
 */
const TemplateStep: FunctionComponent<TemplateStepProps> = ({ values, onChange, onOpenSenderDrawer }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const templatesList = useSelector((state: RootState) => state.emailTemplates.list);
    const sender = useSelector((state: RootState) => state.emailMarketingSender.data);

    const [embeddedEditor, setEmbeddedEditor] = useState<EmbeddedEditor>(null);
    // In flight while a system template is being cloned before opening the
    // editor on the copy. Drives the "Duplicating…" label on the Edit action.
    const [cloningTemplate, setCloningTemplate] = useState<boolean>(false);

    // Resolve the selected template from the templates list so the preview /
    // edit overlay always have a current `EmailTemplate` to work with.
    const selectedTemplate = useMemo<EmailTemplate | null>(() => {
        if (values.emailTemplateId == null) return null;
        return templatesList.find(template => template.id === values.emailTemplateId) ?? null;
    }, [values.emailTemplateId, templatesList]);

    const handleSelectTemplate = (template: EmailTemplate) => {
        if (typeof template.id !== 'number') return;
        onChange({ emailTemplateId: template.id });
    };

    const handleClearTemplate = () => {
        onChange({ emailTemplateId: null });
    };

    const handleStartFromScratch = () => {
        setEmbeddedEditor({ mode: 'create' });
    };

    // System templates can't be edited in place — the BE returns
    // `System templates cannot be edited` on any save. So when the user
    // clicks Edit on a system template we clone it first, retarget the
    // campaign at the clone, and open the editor on the copy. Matches the
    // standalone gallery flow (`EmailTemplates#handleUseSystemTemplate`).
    // Own templates skip the clone and open the editor directly as before.
    const handleEditTemplate = async (template: EmailTemplate) => {
        if (typeof template.id !== 'number') return;

        if (template.editable) {
            setEmbeddedEditor({ mode: 'edit', templateId: template.id });
            return;
        }

        setCloningTemplate(true);
        const cloned = await (dispatch(
            cloneEmailTemplateThunk(apiClientWrapper, template.id)
        ) as unknown as Promise<EmailTemplate | null>);
        setCloningTemplate(false);

        if (cloned && typeof cloned.id === 'number') {
            // Point the campaign at the clone before opening the editor so any
            // subsequent save flows through the clone's ID.
            onChange({ emailTemplateId: cloned.id });
            setEmbeddedEditor({ mode: 'edit', templateId: cloned.id });
        }
        // On failure the thunk already surfaced a toast via alertErrorHandler.
    };

    const handleEmbeddedSave = (template: EmailTemplate) => {
        // Auto-select the freshly saved template so the user goes from the
        // editor straight to the preview — matches the Klaviyo flow.
        if (typeof template.id === 'number') {
            onChange({ emailTemplateId: template.id });
        }
        setEmbeddedEditor(null);
    };

    const handleEmbeddedExit = () => {
        setEmbeddedEditor(null);
    };

    return (
        <div className={styles.root}>
            <div className={styles.formColumn}>
                <TemplateStepForm
                    subject={values.subject}
                    previewText={values.previewText}
                    senderName={values.senderName}
                    sender={sender}
                    onChangeSubject={value => onChange({ subject: value })}
                    onChangePreviewText={value => onChange({ previewText: value })}
                    onChangeSenderName={value => onChange({ senderName: value })}
                    onOpenSenderDrawer={onOpenSenderDrawer}
                />
            </div>

            <div className={styles.pickerColumn}>
                <TemplateStepPicker
                    selectedTemplate={selectedTemplate}
                    onSelectTemplate={handleSelectTemplate}
                    onClearTemplate={handleClearTemplate}
                    onStartFromScratch={handleStartFromScratch}
                    onEditTemplate={handleEditTemplate}
                    cloningTemplate={cloningTemplate}
                />
            </div>

            {embeddedEditor && (
                <EmailTemplateEditorComponent
                    embeddedTemplateId={embeddedEditor.mode === 'edit' ? embeddedEditor.templateId : undefined}
                    onSave={handleEmbeddedSave}
                    onExit={handleEmbeddedExit}
                />
            )}
        </div>
    );
};

export default TemplateStep;
export { TemplateStep };
